// src/padplanner/usePlanLibrary.js
//
// What a plan's layout is drawn and described with: the templates of its
// pinned set and any published standard set (editorPalette.js
// templateSourceIds), and the clearance rules of the rule sets it names. The
// editor and the plan page both load them here (S6a2), so the two read one
// revision with the same templates and the same rules.
//
// A failed read is said, not drawn as nothing: `library.failed` and the rule
// sets' `failed` ids, each with a retry. A failed rule set is not cached, so
// no layout is drawn as having no rules because one read failed.
import { useCallback, useEffect, useMemo, useState } from "react";
import { listTemplateSets, getTemplateSet, getRuleSet } from "../api/padplannerApi";
import { templateSourceIds } from "./editorPalette";

/** {library: {sets, bySet, failed}, templates, templatesById, retry}; nothing is loaded until `enabled`. */
export function usePlanTemplates(plan, enabled) {
  const [library, setLibrary] = useState({ sets: null, bySet: {}, failed: false });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => { setLibrary((l) => ({ ...l, failed: false })); setAttempt((n) => n + 1); }, []);
  const pinnedId = plan?.template_set?.id ?? null;
  useEffect(() => {
    if (!plan || !enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await listTemplateSets();
        const ids = templateSourceIds(plan, list.sets ?? []);
        const loadedSets = await Promise.all(ids.map((id) => getTemplateSet(id).then((r) => [id, r])));
        if (!cancelled) setLibrary({ sets: list.sets ?? [], bySet: Object.fromEntries(loadedSets), failed: false });
      } catch {
        if (!cancelled) setLibrary((l) => ({ ...l, failed: true }));
      }
    })();
    return () => { cancelled = true; };
    // The library changes only with the pin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id, pinnedId, Boolean(plan), enabled, attempt]);
  const templates = useMemo(() => Object.values(library.bySet).flatMap((r) => r?.templates ?? []), [library.bySet]);
  const templatesById = useMemo(() => new Map(templates.map((t) => [t.id, t])), [templates]);
  return { library, setLibrary, templates, templatesById, retry };
}

/**
 * The rules of each rule set named (null ids skipped), fetched once each:
 * {ruleSets: Map(set id -> rules), failed: [set id], retry}. A failed set is
 * left out of the map and named in `failed` until a retry reads it again.
 */
export function useRuleSets(ids) {
  const [ruleSets, setRuleSets] = useState(() => new Map());
  const [failedIds, setFailedIds] = useState(() => new Set());
  const key = ids.filter(Boolean).join(",");
  useEffect(() => {
    const want = key ? key.split(",").filter((id) => !ruleSets.has(id) && !failedIds.has(id)) : [];
    if (!want.length) return undefined;
    let cancelled = false;
    Promise.all(want.map((id) => getRuleSet(id).then((r) => ({ id, rules: r.rules ?? [] }), () => ({ id, failed: true }))))
      .then((got) => {
        if (cancelled) return;
        const ok = got.filter((g) => !g.failed);
        if (ok.length) setRuleSets((m) => new Map([...m, ...ok.map((g) => [g.id, g.rules])]));
        const bad = got.filter((g) => g.failed).map((g) => g.id);
        if (bad.length) setFailedIds((f) => new Set([...f, ...bad]));
      });
    return () => { cancelled = true; };
  }, [key, ruleSets, failedIds]);
  const wanted = key ? key.split(",") : [];
  const failed = wanted.filter((id) => failedIds.has(id));
  const retry = useCallback(() => setFailedIds(new Set()), []);
  return { ruleSets, failed, retry };
}
